// src/pages/Home.js
import React, { useContext } from 'react';
import './Home.css'; // Import the CSS file
import { useTranslation, Trans } from 'react-i18next';
import { FaSearch, FaChartLine, FaHandshake, FaFileAlt, FaChartPie, FaBullseye, FaLightbulb } from 'react-icons/fa';
import { useLanguage } from '../hooks/useLanguage';
import { UserContext } from '../context/UserContext';
import { Link, useNavigate } from 'react-router-dom'; // Import useNavigate
import { NotificationContext } from '../context/NotificationContext'; // Import NotificationContext
import { Role } from '../utils/enums'; // Import Role enum
import mockupReportEn from '../assets/images/mockup_report_en.webp';
import mockupReportHe from '../assets/images/mockup_report_he.webp';

const Home = () => {
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext); // Get addNotification
  const { t } = useTranslation();
  const navigate = useNavigate(); // Get navigate function
  const lang = useLanguage();

  const reportImages = {
    en: mockupReportEn,
    he: mockupReportHe,
  };

  const handleFindOpportunity = () => {
    navigate(`/${lang}/map`); // Navigate to the Map component
  };

  const handleSellProperty = () => {
    if (!user.isLoggedIn) {
      addNotification(
        t('home.notifications.loginToSell'),
        'info',
        0, // 0 for duration
        true, // isPersistent
        [
          { text: t('home.notifications.cancel'), onClick: () => {} },
          { text: t('home.notifications.login'), onClick: () => navigate(`/${lang}/login`), className: 'primary' },
        ]
      );
    } else if (user.role < Role.Seller) {
      const isVerified = user.isEmailVerified && user.isPhoneVerified;
      let message = '';
      let messageKey = '';

      if (isVerified) {
        messageKey = 'home.notifications.becomeSellerVerified';
        message = t(messageKey);
      } else {
        const verificationNeeded = [];
        if (!user.isEmailVerified) verificationNeeded.push(t('home.notifications.verifyEmail'));
        if (!user.isPhoneVerified) verificationNeeded.push(t('home.notifications.verifyPhone'));
        messageKey = 'home.notifications.becomeSellerUnverified';
        message = t(messageKey, { verificationNeeded: verificationNeeded.join(t('home.notifications.and')) });
      }
      addNotification(
        message,
        'info',
        0, // duration
        true, // isPersistent
        [{ text: t('home.notifications.goToPreferences'), onClick: () => navigate(`/${lang}/preferences`), className: 'primary' }]
      );
    } else {
      navigate(`/${lang}/properties?view=my`); // Navigate to the "My Properties" view
    }
  };

  return (
    <>
      <div className="home-container">
        <div className="home-content">
          <h1>{t('home.title')}</h1>
          <h2>{t('home.subtitle')}</h2>
          {user.isLoggedIn ? (
            <p>{t('home.loggedInDescription')}</p>
          ) : (
            <p>
              <Trans i18nKey="home.guestDescription">
                Jump right-in, or
                <Link to={`/${lang}/login`} className="home-link">
                  sign-in
                </Link>{' '}
                to unlock full features.
              </Trans>
            </p>
          )}

          <h2 className="home-action-prompt">{t('home.actionPrompt')}</h2>
          <div className="home-buttons-container">
            <div className="home-button-wrapper">
              <button className="home-action-button" onClick={handleFindOpportunity}>
                <div className="button-line-one">{t('home.buyButton')}</div>
                <div className="button-line-two">{t('home.buySubtitle')}</div>
              </button>
              <div className="button-subtitle">{t('home.buyDescription')}</div>
            </div>
            <div className="home-button-wrapper">
              <button className="home-action-button" onClick={handleSellProperty}>
                <div className="button-line-one">{t('home.sellButton')}</div>
                <div className="button-line-two">{t('home.sellSubtitle')}</div>
              </button>
              <div className="button-subtitle">{t('home.sellDescription')}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="how-it-works-section">
        <div className="how-it-works-content">
          <h2>{t('home.howItWorksTitle')}</h2>
          <div className="steps-container">
            <div className="step">
              <div className="step-icon-container">
                <FaSearch />
              </div>
              <h3>{t('home.step1Title')}</h3>
              <p>{t('home.step1Text')}</p>
            </div>
            <div className="step">
              <div className="step-icon-container">
                <FaChartLine />
              </div>
              <h3>{t('home.step2Title')}</h3>
              <p>{t('home.step2Text')}</p>
            </div>
            <div className="step">
              <div className="step-icon-container">
                <FaHandshake />
              </div>
              <h3>{t('home.step3Title')}</h3>
              <p>{t('home.step3Text')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="report-highlight-section">
        <div className="report-highlight-content">
          <h2>{t('home.reportSectionTitle')}</h2>
          <div className="report-columns">
            <div className="report-visual-column">
              <img
                src={reportImages[lang] || reportImages.en}
                alt={t('home.reportSectionTitle')}
                className="report-image-mockup"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = reportImages.en;
                }}
              />
            </div>
            <div className="report-text-column">
              <h3>{t('home.reportSectionSubtitle')}</h3>
              <ul className="benefits-list">
                <li>
                  <FaFileAlt className="benefit-icon" />
                  <div>
                    <strong>{t('home.reportBenefit1Title')}</strong>
                    <p>{t('home.reportBenefit1Text')}</p>
                  </div>
                </li>
                <li>
                  <FaChartPie className="benefit-icon" />
                  <div>
                    <strong>{t('home.reportBenefit2Title')}</strong>
                    <p>{t('home.reportBenefit2Text')}</p>
                  </div>
                </li>
                <li>
                  <FaBullseye className="benefit-icon" />
                  <div>
                    <strong>{t('home.reportBenefit3Title')}</strong>
                    <p>{t('home.reportBenefit3Text')}</p>
                  </div>
                </li>
                <li>
                  <FaLightbulb className="benefit-icon" />
                  <div>
                    <strong>{t('home.reportBenefit4Title')}</strong>
                    <p>{t('home.reportBenefit4Text')}</p>
                  </div>
                </li>
              </ul>
              <button className="report-cta-button" onClick={handleFindOpportunity}>
                {t('home.reportSectionCTA')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
